/**
 * Pharmacy dispense lifecycle helpers.
 * Create → Get Items → Verify → Reserve → Allocate → Deliver
 */
import dayjs from 'dayjs';
import {
  createDispense,
  getDispenseItems,
  verifyDispense,
  reserveDispense,
  allocateDispense,
  deliverDispense,
} from '../api/dispensing';
import { createControlledAuth, getControlledQueue } from '../api/pharmacy';
import { resolveDispensingScope, formatDispensingError } from './dispensingEntity';
import {
  isControlledOrNarcotic,
  buildPharmacistAuthorization,
  isControlledAuthApproved,
} from './controlledDrug';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function ss(key) {
  const v = sessionStorage.getItem(key);
  return v && v !== 'null' && v !== 'undefined' ? v : null;
}

function asStringQty(value) {
  if (value == null || value === '') return '';
  return String(value);
}

function asStringId(value) {
  if (value == null || value === '') return '';
  return String(value);
}

export function isUuid(value) {
  return UUID_RE.test(String(value || '').trim());
}

/**
 * Extracts the GST already embedded in a tax-inclusive amount (MRP/sale price
 * are tax-inclusive by law) — used to show a compliance-style tax breakup on
 * dispense/bill screens. Returns { taxable, tax }, both 0 when amount or
 * gstRate is falsy (e.g. gst_rate not yet available from the item master).
 */
export function splitInclusiveTax(amount, gstRate) {
  const amt = Number(amount) || 0;
  const rate = Number(gstRate) || 0;
  if (!amt || !rate) return { taxable: amt, tax: 0 };
  const taxable = amt / (1 + rate / 100);
  return { taxable, tax: amt - taxable };
}

function pickRaw(row, ...keys) {
  if (!row || typeof row !== 'object') return '';
  for (const key of keys) {
    const value = row[key];
    if (value != null && value !== '') return value;
  }
  return '';
}

/** Resolve org scope for stage query params (store row preferred). */
export function resolvePharmacyDispenseScope(header = {}, stores = []) {
  return resolveDispensingScope(
    {
      tenant_id: header.tenant_id,
      hospital_id: header.hospital_id,
      from_store_id: header.from_store_id,
    },
    stores,
  );
}

/**
 * Build Create (POST /inventory/pharmacy/dispensing/) body from prescription + UI lines.
 * Quantities and rates are strings per API contract.
 */
export function buildCreateDispenseBody({
  prescription,
  storeId,
  lines = [],
  stores = [],
  dispenseType,
  isDirectDispense = false,
  remarks,
  priority = 'NORMAL',
  transactionNumber,
  pharmacistAuthorization = null,
} = {}) {
  const raw = prescription?._raw || prescription || {};
  const actor = ss('USER_ACCESS_KEY');
  const store = (stores || []).find(
    (s) => String(s.store_id || s.id) === String(storeId),
  );
  const scope = resolvePharmacyDispenseScope(
    {
      tenant_id: prescription?.tenant_id || pickRaw(raw, 'tenant_id') || store?.tenant_id,
      hospital_id: prescription?.hospital_id || pickRaw(raw, 'hospital_id') || store?.hospital_id,
      from_store_id: storeId,
    },
    stores,
  );

  const patientId = asStringId(
    prescription?.patient_id || pickRaw(raw, 'patient_id', 'uhid', 'external_patient_id'),
  );
  const doctorId = asStringId(prescription?.doctor_id || pickRaw(raw, 'doctor_id'));
  const admissionRaw = prescription?.admission_id ?? pickRaw(raw, 'admission_id');
  // Gateway requires string (not number) — coerce always when present
  const admissionId = admissionRaw != null && admissionRaw !== '' ? asStringId(admissionRaw) : '';
  const billingAccountId = asStringId(
    pickRaw(raw, 'billing_account_id', 'billing_account')
    || prescription?.billing_account_id,
  ).trim();

  const explicitType = dispenseType || pickRaw(raw, 'dispense_type');
  const type = explicitType
    || (admissionId || pickRaw(raw, 'ipd_flag') ? 'IPD' : 'OPD');

  // Only an explicitly-supplied number is sent — never one derived from the
  // prescription. Defaulting this to `order_no` made transaction_number unique per
  // ORDER, so the service's 409 "Transaction with transaction_number='ORD-...'
  // already exists" fired on every second create against the same prescription:
  // a retry after any post-create failure, or a legitimate second/partial fill.
  // Once one existed, that order could never be dispensed again.
  //
  // Omitting it lets dispensing-billing-service allocate its own sequential
  // DISP-FY2627-000001 (transaction_number is Optional on DispenseCreate; see
  // DispenseService._auto_generate_number, which retries on collision and is scoped
  // per tenant+hospital). The order stays traceable through admission_id,
  // patient_id, source_type=CPOE_PRESCRIPTION and remarks.
  const txnNumber = asStringId(transactionNumber);

  const items = (lines || [])
    .filter((l) => l.item_id && Number(l.dispense_qty ?? l.quantity) > 0)
    .map((l) => {
      const qty = asStringQty(l.dispense_qty ?? l.quantity);
      const rate = asStringQty(l.unit_rate ?? l.rate ?? '0');
      const uomId = l.uom_id || l.dispense_uom_id || l.base_uom_id;
      const batchNumber = String(l.batch_number || '').trim();
      const useFefo = !batchNumber;
      const item = {
        item_id: l.item_id,
        uom_id: uomId,
        requested_quantity: qty,
        unit_rate: rate || '0',
        use_fefo: useFefo,
      };
      if (batchNumber) {
        item.batch_number = batchNumber;
      }
      if (l.tax_percent != null && l.tax_percent !== '') {
        item.tax_percent = asStringQty(l.tax_percent);
      }
      if (l.discount_percent != null && l.discount_percent !== '') {
        item.discount_percent = asStringQty(l.discount_percent);
      }
      if (l.remarks || l.instructions) {
        item.remarks = l.remarks || l.instructions;
      }
      return item;
    });

  // EMR-only sidecar, stripped by the gateway before the payload reaches the
  // pharmacy service. It maps each dispensed line back to the order-book row it
  // fills, which is the only way the deliver/confirm hooks can credit the right
  // order: source_reference_id takes a UUID and IPD order ids are integers, so
  // the transaction carries no usable back-link.
  //
  // Without this the dispense still succeeds — but the order's counters are
  // never credited and it never leaves the pharmacy queue, which is the whole
  // bug this exists to fix. The hooks report such rows under
  // `order_fulfilment.skipped` rather than guessing a quantity.
  //
  // IPD ONLY. An emergency take-home script has no admission, and its
  // `medicines[].id` is an emergency_order id, not a Prescription id — writing
  // those into the link table would point at unrelated rows. Emergency lines
  // transition per line by their own id instead (no quantities to credit).
  const orderLines = admissionId
    ? (lines || [])
      .filter((l) => l.item_id && Number(l.dispense_qty ?? l.quantity) > 0)
      // A synthetic fallback id ("rx-med-0", from mapPrescriptionItem when the
      // row carried none) identifies nothing — a link built on it would be a
      // dangling reference, so those lines are left unlinked and visible.
      .filter((l) => /^\d+$/.test(String(l.id ?? '').trim()))
      .map((l) => ({
        prescription_id: Number(l.id),
        item_id: l.item_id,
        requested_quantity: asStringQty(l.dispense_qty ?? l.quantity),
      }))
    : [];

  const body = {
    from_store_id: storeId,
    patient_id: patientId,
    doctor_id: doctorId,
    dispense_type: type || 'OPD',
    transaction_date: dayjs().format('YYYY-MM-DD'),
    priority: priority || prescription?.priority || 'NORMAL',
    created_by: actor,
    is_direct_dispense: Boolean(isDirectDispense),
    // MRP/sale-price lines are always tax-inclusive — GST is extracted from
    // within unit_rate, never added on top (see splitInclusiveTax above).
    tax_inclusive: true,
    items,
  };

  // Omitted, never sent empty: the field is `min_length=1` server-side, so a blank
  // string is a 422 where an absent key is the documented "you number it" signal.
  if (txnNumber) body.transaction_number = txnNumber;

  // Omitted when empty so the gateway never has to distinguish "no linkable
  // lines" from "an older client that does not send this".
  if (orderLines.length) body.order_lines = orderLines;

  // Org fields: include when known (store/prescription). Gateway may also inject from ORG-CODE.
  if (scope.tenant_id) body.tenant_id = scope.tenant_id;
  if (scope.hospital_id) body.hospital_id = scope.hospital_id;
  if (billingAccountId) body.billing_account_id = billingAccountId;
  if (admissionId) body.admission_id = admissionId;
  if (remarks) body.remarks = remarks;
  if (prescription?.id || pickRaw(raw, 'id', 'prescription_id', 'order_no')) {
    body.source_type = 'CPOE_PRESCRIPTION';
    const sourceRef = prescription?.id || pickRaw(raw, 'id', 'prescription_id');
    if (isUuid(sourceRef)) {
      body.source_reference_id = sourceRef;
    }
  }

  const hasControlled = (lines || []).some((l) => isControlledOrNarcotic(l));
  if (hasControlled && pharmacistAuthorization) {
    body.transaction_payload = { pharmacist_authorization: pharmacistAuthorization };
  }

  return { body, scope, missing: validateCreateBody(body) };
}

function validateCreateBody(body) {
  const missing = [];
  // transaction_number is intentionally not required — the service numbers the
  // transaction when we omit it (see buildCreateDispenseBody).
  if (!body.from_store_id) missing.push('pharmacy store');
  if (!body.patient_id) missing.push('patient');
  if (!body.doctor_id) missing.push('doctor');
  if (!body.created_by) missing.push('current user');
  if (!body.items?.length) missing.push('medicines');
  const badUom = (body.items || []).some((i) => !i.uom_id);
  if (badUom) missing.push('unit of measure (select store so stock can resolve UOM)');
  const badItem = (body.items || []).some((i) => !i.item_id);
  if (badItem) missing.push('catalogue item match');
  return missing;
}

/** Resolve batch/expiry from dispense line items (GET /items shapes vary by stage). */
export function resolveDispenseLineBatch(item) {
  if (!item || typeof item !== 'object') return { batchNumber: null, expiryDate: null };

  const topBatch = item.batch_number || item.batch || item.reserved_batch_number
    || item.fill_batch_number || item.allocation_batch_number || null;
  const topExpiry = item.expiry_date || item.batch_expiry_date || null;
  if (topBatch) return { batchNumber: topBatch, expiryDate: topExpiry };

  const nestedSources = [
    ...(Array.isArray(item.batches) ? item.batches : []),
    ...(Array.isArray(item.allocations) ? item.allocations : []),
    ...(Array.isArray(item.reservations) ? item.reservations : []),
    ...(Array.isArray(item.reserve_lines) ? item.reserve_lines : []),
  ];

  for (const nested of nestedSources) {
    if (!nested || typeof nested !== 'object') continue;
    const batch = nested.batch_number || nested.batch || nested.reserved_batch_number;
    if (batch) {
      return {
        batchNumber: batch,
        expiryDate: nested.expiry_date || nested.batch_expiry_date || topExpiry,
      };
    }
  }

  return { batchNumber: null, expiryDate: topExpiry };
}

/** Map UI lines → reserve lines[] using transaction item ids from GET /items. */
export function buildReserveLines(uiLines = [], txnItems = []) {
  const byItemId = new Map();
  (txnItems || []).forEach((row) => {
    if (row?.item_id != null) byItemId.set(String(row.item_id), row);
  });

  return (uiLines || [])
    .filter((l) => l.item_id && Number(l.dispense_qty ?? l.quantity) > 0)
    .map((l) => {
      const txnItem = byItemId.get(String(l.item_id));
      const transactionItemId = txnItem?.id;
      const uomId = l.dispense_uom_id || l.uom_id || l.base_uom_id || txnItem?.uom_id;
      const qty = asStringQty(l.dispense_qty ?? l.quantity);
      const line = {
        transaction_item_id: transactionItemId,
        batch_number: l.batch_number || null,
        dispense_uom_id: uomId,
        dispense_quantity: qty,
        barcode_value: l.barcode_value || null,
        substitute_for_item_id: l.substitute_for_item_id || null,
        controlled_auth_id: l.controlled_auth_id || null,
      };
      return line;
    });
}

/** Map AUTHORIZED controlled-auth records to UI lines by transaction item id. */
export async function resolveControlledAuthIdsForLines(transactionId, txnItems = [], scope = {}) {
  const queue = await getControlledQueue({
    tenant_id: scope.tenant_id,
    hospital_id: scope.hospital_id,
    status: 'AUTHORIZED',
  }).catch(() => []);
  const rows = Array.isArray(queue) ? queue : (queue?.items || []);

  const byTxnItemId = new Map();
  rows.forEach((row) => {
    const txnId = row.inventory_transaction_id || row.transaction_id;
    if (txnId && String(txnId) !== String(transactionId)) return;
    const txnItemId = row.inventory_transaction_item_id || row.transaction_item_id;
    const authId = row.auth_id || row.id;
    if (txnItemId && authId && isControlledAuthApproved(row.status)) {
      byTxnItemId.set(String(txnItemId), authId);
    }
  });

  return (txnItems || []).map((txnItem) => {
    const authId = byTxnItemId.get(String(txnItem.id)) || txnItem.controlled_auth_id || null;
    return { ...txnItem, controlled_auth_id: authId };
  });
}

function mergeAuthIntoUiLines(uiLines = [], txnItems = []) {
  const byItemId = new Map();
  (txnItems || []).forEach((row) => {
    if (row?.item_id != null) byItemId.set(String(row.item_id), row);
  });
  return (uiLines || []).map((line) => {
    const txnItem = byItemId.get(String(line.item_id));
    return {
      ...line,
      controlled_auth_id: line.controlled_auth_id || txnItem?.controlled_auth_id || null,
    };
  });
}

async function submitControlledAuthRequests({
  transactionId,
  txnItems = [],
  uiLines = [],
  scope = {},
  reason = 'IPD controlled medicine dispense',
}) {
  const pharmacistId = ss('USER_ACCESS_KEY');
  const byItemId = new Map();
  (txnItems || []).forEach((row) => {
    if (row?.item_id != null) byItemId.set(String(row.item_id), row);
  });

  const requests = [];
  for (const line of uiLines) {
    if (!isControlledOrNarcotic(line)) continue;
    const txnItem = byItemId.get(String(line.item_id));
    if (!txnItem?.id) continue;
    const body = {
      tenant_id: scope.tenant_id,
      hospital_id: scope.hospital_id,
      inventory_transaction_id: transactionId,
      inventory_transaction_item_id: txnItem.id,
      item_id: line.item_id,
      quantity: Number(line.dispense_qty ?? line.quantity) || 0,
      reason,
      requested_by: pharmacistId,
    };
    const created = await createControlledAuth(body, scope);
    requests.push({
      line,
      txnItem,
      authId: created?.id || created?.auth_id,
    });
  }
  return requests;
}

/**
 * Staged flow with controlled lines: Create → auth requests → Verify (no reserve).
 * Reserve happens later once supervisor approves all controlled auths.
 */
export async function runCreateVerifyControlled({
  prescription,
  storeId,
  lines,
  stores = [],
  allergyChecked = true,
  doseChecked = true,
  verificationNotes = '',
  remarks,
  pharmacistAuthorization = null,
  authReason,
} = {}) {
  const { body, scope, missing } = buildCreateDispenseBody({
    prescription,
    storeId,
    lines,
    stores,
    remarks,
    isDirectDispense: false,
    pharmacistAuthorization,
  });

  if (missing.length) {
    const err = new Error(`Missing required fields: ${missing.join(', ')}`);
    err.code = 'VALIDATION';
    throw err;
  }

  let transaction;
  try {
    transaction = await createDispense(body);
  } catch (e) {
    throw Object.assign(new Error(formatDispensingError(e, 'Failed to create dispense')), { cause: e });
  }

  const transactionId = transaction?.id || transaction?.transaction_id;
  if (!transactionId) {
    throw new Error('Create succeeded but no transaction id was returned');
  }

  const stageScope = {
    ...scope,
    tenant_id: scope.tenant_id || transaction.tenant_id,
    hospital_id: scope.hospital_id || transaction.hospital_id,
  };

  let items;
  try {
    const itemsPayload = await getDispenseItems(transactionId, stageScope);
    items = Array.isArray(itemsPayload) ? itemsPayload : (itemsPayload?.items || []);
  } catch (e) {
    throw Object.assign(
      new Error(formatDispensingError(e, 'Failed to load dispense line items')),
      { cause: e, transactionId },
    );
  }

  const selectedUi = (lines || []).filter(
    (l) => l.item_id && Number(l.dispense_qty ?? l.quantity) > 0,
  );

  let authRequests = [];
  const hasControlled = selectedUi.some((l) => isControlledOrNarcotic(l));
  if (hasControlled) {
    try {
      authRequests = await submitControlledAuthRequests({
        transactionId,
        txnItems: items,
        uiLines: selectedUi,
        scope: stageScope,
        reason: authReason || `Controlled dispense for ${prescription?.order_number || transactionId}`,
      });
    } catch (e) {
      throw Object.assign(
        new Error(formatDispensingError(e, 'Failed to submit controlled medicine authorization')),
        { cause: e, transactionId },
      );
    }
  }

  try {
    await verifyDispense(transactionId, {
      allergy_checked: allergyChecked,
      dose_checked: doseChecked,
      verification_notes: verificationNotes || 'Prescription and dose verified',
    }, stageScope);
  } catch (e) {
    throw Object.assign(
      new Error(formatDispensingError(e, 'Verification failed')),
      { cause: e, transactionId },
    );
  }

  return {
    transactionId,
    transaction,
    items,
    scope: stageScope,
    pendingControlledAuth: hasControlled,
    authRequests,
  };
}

/** Reserve stock for a verified dispense — attaches AUTHORIZED controlled_auth_id per line. */
export async function runReserveDispenseStock({
  transactionId,
  uiLines = [],
  txnItems = [],
  scope = {},
} = {}) {
  let enrichedTxnItems = txnItems;
  if (!enrichedTxnItems?.length) {
    const itemsPayload = await getDispenseItems(transactionId, scope);
    enrichedTxnItems = Array.isArray(itemsPayload) ? itemsPayload : (itemsPayload?.items || []);
  }

  enrichedTxnItems = await resolveControlledAuthIdsForLines(transactionId, enrichedTxnItems, scope);
  const mergedUi = mergeAuthIntoUiLines(uiLines, enrichedTxnItems);

  const controlledMissing = mergedUi.filter(
    (l) => isControlledOrNarcotic(l) && !l.controlled_auth_id,
  );
  if (controlledMissing.length) {
    throw Object.assign(
      new Error('Controlled medicines require supervisor authorization before reserve'),
      { code: 'CONTROLLED_AUTH_PENDING', transactionId },
    );
  }

  const reserveLines = buildReserveLines(mergedUi, enrichedTxnItems);
  const incomplete = reserveLines.filter((l) => !l.transaction_item_id || !l.dispense_uom_id);
  if (incomplete.length) {
    throw Object.assign(
      new Error('Could not match prescription lines to transaction items. Reload and try again.'),
      { transactionId },
    );
  }

  try {
    return await reserveDispense(transactionId, { lines: reserveLines }, scope);
  } catch (e) {
    const status = e?.status ?? e?.response?.status;
    const msg = formatDispensingError(e, 'Failed to reserve stock');
    throw Object.assign(new Error(msg), {
      cause: e,
      transactionId,
      code: status === 403 ? 'CONTROLLED_AUTH_REQUIRED' : undefined,
    });
  }
}

export function canReserveDispense(dispense) {
  if (!dispense) return false;
  const status = String(dispense.status || '').toUpperCase();
  const stage = resolveDispenseStage(dispense);
  if (status === 'RESERVED') return false;
  if (stage === 'FILL' || stage === 'ALLOCATE' || stage === 'DELIVER') return false;
  return status === 'VERIFIED' || status === 'DRAFT' || stage === 'VERIFICATION' || !stage;
}

/**
 * Run Create → Get Items → Verify → Reserve.
 * Returns { transaction, items, reserved }.
 */
export async function runCreateVerifyReserve({
  prescription,
  storeId,
  lines,
  stores = [],
  allergyChecked = true,
  doseChecked = true,
  verificationNotes = '',
  remarks,
  isDirectDispense = false,
  pharmacistAuthorization = null,
} = {}) {
  const { body, scope, missing } = buildCreateDispenseBody({
    prescription,
    storeId,
    lines,
    stores,
    remarks,
    isDirectDispense,
    pharmacistAuthorization,
  });

  if (missing.length) {
    const err = new Error(`Missing required fields: ${missing.join(', ')}`);
    err.code = 'VALIDATION';
    throw err;
  }

  let transaction;
  try {
    transaction = await createDispense(body);
  } catch (e) {
    throw Object.assign(new Error(formatDispensingError(e, 'Failed to create dispense')), { cause: e });
  }

  const transactionId = transaction?.id || transaction?.transaction_id;
  if (!transactionId) {
    throw new Error('Create succeeded but no transaction id was returned');
  }

  const stageScope = {
    ...scope,
    tenant_id: scope.tenant_id || transaction.tenant_id,
    hospital_id: scope.hospital_id || transaction.hospital_id,
  };

  let items;
  try {
    const itemsPayload = await getDispenseItems(transactionId, stageScope);
    items = Array.isArray(itemsPayload) ? itemsPayload : (itemsPayload?.items || []);
  } catch (e) {
    throw Object.assign(
      new Error(formatDispensingError(e, 'Failed to load dispense line items')),
      { cause: e, transactionId },
    );
  }

  try {
    await verifyDispense(transactionId, {
      allergy_checked: allergyChecked,
      dose_checked: doseChecked,
      verification_notes: verificationNotes || 'Prescription and dose verified',
    }, stageScope);
  } catch (e) {
    throw Object.assign(
      new Error(formatDispensingError(e, 'Verification failed')),
      { cause: e, transactionId },
    );
  }

  const selectedUi = (lines || []).filter(
    (l) => l.item_id && Number(l.dispense_qty ?? l.quantity) > 0,
  );
  const reserveLines = buildReserveLines(selectedUi, items);
  const incomplete = reserveLines.filter((l) => !l.transaction_item_id || !l.dispense_uom_id);
  if (incomplete.length) {
    throw Object.assign(
      new Error('Could not match prescription lines to transaction items. Reload and try again.'),
      { transactionId },
    );
  }

  const controlledMissing = selectedUi.some(
    (l) => isControlledOrNarcotic(l) && !l.controlled_auth_id,
  );
  if (controlledMissing) {
    throw Object.assign(
      new Error('Controlled medicines require authorization before reserve'),
      { transactionId },
    );
  }

  let reserved;
  try {
    reserved = await reserveDispense(transactionId, { lines: reserveLines }, stageScope);
  } catch (e) {
    throw Object.assign(
      new Error(formatDispensingError(e, 'Failed to reserve stock')),
      { cause: e, transactionId },
    );
  }

  try {
    const itemsPayload = await getDispenseItems(transactionId, stageScope);
    items = Array.isArray(itemsPayload) ? itemsPayload : (itemsPayload?.items || items);
  } catch {
    // keep pre-reserve items if refresh fails
  }

  return {
    transactionId,
    transaction: reserved || transaction,
    items,
    scope: stageScope,
  };
}

export async function runAllocateDispense(transactionId, opts = {}, scope = {}) {
  try {
    return await allocateDispense(transactionId, {
      allocation_notes: opts.allocation_notes || 'Allocated to patient',
      allocated_by: opts.allocated_by,
    }, scope);
  } catch (e) {
    throw Object.assign(new Error(formatDispensingError(e, 'Allocation failed')), { cause: e });
  }
}

export async function runDeliverDispense(transactionId, opts = {}, scope = {}) {
  if (!opts.handover_to) {
    throw new Error('Handover recipient is required to deliver');
  }
  try {
    return await deliverDispense(transactionId, {
      delivered_by: opts.delivered_by,
      handover_to: opts.handover_to,
    }, scope);
  } catch (e) {
    throw Object.assign(new Error(formatDispensingError(e, 'Delivery failed')), { cause: e });
  }
}

/** Stage helpers for DispenseDetail UI. */
export function resolveDispenseStage(dispense) {
  if (!dispense) return null;
  return dispense.dispense_stage || null;
}

export function canAllocateDispense(dispense) {
  if (!dispense) return false;
  return dispense.status === 'RESERVED' && resolveDispenseStage(dispense) === 'FILL';
}

export function canDeliverDispense(dispense) {
  if (!dispense) return false;
  const stage = resolveDispenseStage(dispense);
  return dispense.status === 'RESERVED' && (stage === 'ALLOCATE' || stage === 'ALLOCATED');
}

export { formatDispensingError };
